import { loadInventory } from '../util/inventory-loader.ts';
import { buildVehicleCard, initPrequalTriggers } from '../util/vehicle-card.ts';
import { calcMonthly } from '../util/format-utils.ts';
import type { StaticInventoryItem } from '@flexpath/core/src/types/staticInventory-types.d.ts';

const PAGE_SIZE = 12;
let allVehicles: StaticInventoryItem[] = [];
let currentCondition = ''; // '', 'N', 'U', 'C'
let currentPage = 1;
let searchQuery = '';

/* ── Body style matching ── */
const matchesBodyStyle = (v: StaticInventoryItem, style: string): boolean => {
  if (style === 'suv')     return v.fpBodyStyle === 'suv';
  if (style === 'truck')   return v.fpBodyStyle === 'pickup';
  if (style === 'sedan')   return v.fpBodyStyle === 'sedan';
  if (style === 'coupe')   return v.fpBodyStyle === 'coupe' || v.fpBodyStyle === 'convertible';
  if (style === 'van')     return v.fpBodyStyle === 'van' || v.fpBodyStyle === 'minivan';
  if (style === 'electric') return !!(v.fuel && (v.fuel.toLowerCase().includes('electric') || v.fuel.toLowerCase().includes('hybrid')));
  return false;
};

/* ── Exterior color matching ── */
const matchesColor = (v: StaticInventoryItem, color: string): boolean => {
  const c = (v.colorExt || '').toLowerCase();
  if (color === 'black')        return c.indexOf('black') !== -1;
  if (color === 'white')        return c.indexOf('white') !== -1;
  if (color === 'silver-gray')  return c.indexOf('silver') !== -1 || c.indexOf('gray') !== -1 || c.indexOf('grey') !== -1;
  if (color === 'red')          return c.indexOf('red') !== -1;
  if (color === 'blue')         return c.indexOf('blue') !== -1;
  if (color === 'other')        return !!c && ['black','white','silver','gray','grey','red','blue'].every((k) => c.indexOf(k) === -1);
  return false;
};

/* ── Filtering ── */
const getFiltered = (): StaticInventoryItem[] => {
  const make     = (document.getElementById('filter-make') as HTMLSelectElement | null)?.value || '';
  const model    = (document.getElementById('filter-model') as HTMLSelectElement | null)?.value || '';
  const yearMin  = parseInt((document.getElementById('filter-year-min') as HTMLInputElement | null)?.value ?? '') || 0;
  const yearMax  = parseInt((document.getElementById('filter-year-max') as HTMLInputElement | null)?.value ?? '') || 0;
  const priceMin = parseInt((document.getElementById('filter-price-min') as HTMLInputElement | null)?.value ?? '') || 0;
  const priceMax = parseInt((document.getElementById('filter-price-max') as HTMLInputElement | null)?.value ?? '') || 0;
  const maxMiles = parseInt((document.getElementById('filter-mileage') as HTMLInputElement | null)?.value ?? '') || 0;
  const maxPay   = parseInt((document.getElementById('filter-payment') as HTMLInputElement | null)?.value ?? '') || 0;
  const styles   = Array.from(document.querySelectorAll<HTMLInputElement>('.body-style-check:checked')).map((c) => c.value);
  const colors   = Array.from(document.querySelectorAll<HTMLInputElement>('.color-check:checked')).map((c) => c.value);
  const q = searchQuery.toLowerCase();

  return allVehicles.filter((v) => {
    if (currentCondition && v.condition !== currentCondition) return false;
    if (make  && v.make  !== make)  return false;
    if (model && v.model !== model) return false;
    if (yearMin && v.year < yearMin) return false;
    if (yearMax && v.year > yearMax) return false;
    if (priceMin && (v.priceMap ?? 0) < priceMin) return false;
    if (priceMax && (v.priceMap ?? 0) > priceMax) return false;
    if (maxMiles && v.miles > maxMiles) return false;
    if (maxPay && calcMonthly(v.priceMap ?? 0) > maxPay) return false;
    if (styles.length && !styles.some((s) => matchesBodyStyle(v, s))) return false;
    if (colors.length && !colors.some((c) => matchesColor(v, c))) return false;
    if (q) {
      const hay = (v.year + ' ' + v.make + ' ' + v.model + ' ' + v.trim + ' ' + v.style + ' ' + v.stock).toLowerCase();
      if (hay.indexOf(q) === -1) return false;
    }
    return true;
  });
};

/* ── Sorting ── */
const getSorted = (vehicles: StaticInventoryItem[]): StaticInventoryItem[] => {
  const sortVal = (document.querySelector('.sort-select') as HTMLSelectElement | null)?.value || '';
  return vehicles.slice().sort((a, b) => {
    if (sortVal === 'Price: Low to High')  return (a.priceMap ?? 0) - (b.priceMap ?? 0);
    if (sortVal === 'Price: High to Low')  return (b.priceMap ?? 0) - (a.priceMap ?? 0);
    if (sortVal === 'Year: Newest First')  return b.year - a.year;
    if (sortVal === 'Mileage: Lowest First') return a.miles - b.miles;
    if (sortVal === 'Payment: Low to High') return calcMonthly(a.priceMap ?? 0) - calcMonthly(b.priceMap ?? 0);
    return b.year - a.year; // default: newest first
  });
};

/* ── Render ── */
const render = (): void => {
  const filtered = getSorted(getFiltered());
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (currentPage > totalPages) currentPage = totalPages;
  const start = (currentPage - 1) * PAGE_SIZE;
  const page  = filtered.slice(start, start + PAGE_SIZE);

  // Count
  const countEl = document.querySelector('.inv-count');
  if (countEl) {
    countEl.innerHTML = total === 0
      ? '<strong>0</strong> vehicles found'
      : 'Showing <strong>' + (start + 1) + '–' + Math.min(start + PAGE_SIZE, total) + '</strong> of <strong>' + total + '</strong> vehicles';
  }

  // Grid
  const grid = document.getElementById('inventoryGrid');
  // Original indexes grid.innerHTML unconditionally; preserve behavior by guarding the null case.
  if (grid) {
    if (page.length === 0) {
      grid.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:60px 20px;">'
        + '<p style="color:var(--secondary);font-size:1rem;">No vehicles match your criteria. '
        + '<a href="inventory.html" style="color:var(--accent1);">Clear all filters</a></p></div>';
    } else {
      grid.innerHTML = page.map((v) => buildVehicleCard(v)).join('');
      initPrequalTriggers(grid);
    }
  }

  renderChips();
  renderPagination(totalPages);
  updateBodyStyleCounts();
  updateConditionTabs();
};

/* ── Active filter chips ── */
const renderChips = (): void => {
  const container = document.getElementById('activeFilters');
  if (!container) return;
  const chips: string[] = [];
  if (currentCondition) {
    const lbl = ({ N: 'New', U: 'Used', C: 'Certified' } as Record<string, string>)[currentCondition] || currentCondition;
    chips.push(chip(lbl, 'condition', ''));
  }
  const makeEl = document.getElementById('filter-make') as HTMLSelectElement | null;
  if (makeEl && makeEl.value) chips.push(chip(makeEl.value, 'el', 'filter-make'));
  const modelEl = document.getElementById('filter-model') as HTMLSelectElement | null;
  if (modelEl && modelEl.value) chips.push(chip(modelEl.value, 'el', 'filter-model'));
  document.querySelectorAll<HTMLInputElement>('.body-style-check:checked').forEach((cb) => {
    const lbl = cb.closest('label')?.textContent?.trim().split('\n')[0].trim() ?? '';
    chips.push(chip(lbl, 'cb', cb.value));
  });
  document.querySelectorAll<HTMLInputElement>('.color-check:checked').forEach((cb) => {
    const lbl = cb.closest('label')?.textContent?.trim().split('\n')[0].trim() ?? '';
    chips.push(chip(lbl, 'color-cb', cb.value));
  });
  container.innerHTML = chips.join('');
  container.querySelectorAll<HTMLButtonElement>('.chip-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.type, v = btn.dataset.val;
      if (t === 'condition') { currentCondition = ''; }
      else if (t === 'el') {
        const el = v ? (document.getElementById(v) as HTMLInputElement | HTMLSelectElement | null) : null;
        if (el) { el.value = ''; if (v === 'filter-make') populateModels(''); }
      } else if (t === 'cb') {
        const cb = document.querySelector<HTMLInputElement>('.body-style-check[value="' + v + '"]');
        if (cb) cb.checked = false;
      } else if (t === 'color-cb') {
        const colorCb = document.querySelector<HTMLInputElement>('.color-check[value="' + v + '"]');
        if (colorCb) colorCb.checked = false;
      }
      currentPage = 1;
      render();
    });
  });
};

const chip = (label: string, type: string, val: string): string => {
  return '<div class="filter-chip">' + label
    + ' <button class="chip-remove" data-type="' + type + '" data-val="' + val + '" aria-label="Remove filter">✕</button></div>';
};

/* ── Pagination ── */
const renderPagination = (totalPages: number): void => {
  const container = document.getElementById('pagination');
  if (!container) return;
  if (totalPages <= 1) { container.innerHTML = ''; return; }
  const pages = getPagesToShow(currentPage, totalPages);
  let html = '<button class="page-btn ' + (currentPage === 1 ? 'disabled' : '') + '" data-p="' + (currentPage - 1) + '">◀</button>';
  let last = 0;
  pages.forEach((p) => {
    if (p - last > 1) html += '<span style="padding:0 6px;color:var(--secondary);">…</span>';
    html += '<button class="page-btn ' + (p === currentPage ? 'active' : '') + '" data-p="' + p + '">' + p + '</button>';
    last = p;
  });
  html += '<button class="page-btn ' + (currentPage === totalPages ? 'disabled' : '') + '" data-p="' + (currentPage + 1) + '">▶</button>';
  container.innerHTML = html;
  container.querySelectorAll<HTMLButtonElement>('.page-btn:not(.disabled)').forEach((btn) => {
    btn.addEventListener('click', () => {
      const p = parseInt(btn.dataset.p ?? '');
      if (p >= 1 && p <= totalPages) {
        currentPage = p;
        render();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  });
};

const getPagesToShow = (cur: number, total: number): number[] => {
  const s = new Set<number>([1, total]);
  for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) s.add(i);
  return Array.from(s).sort((a, b) => a - b);
};

/* ── Make → Model cascade ── */
const populateMakes = (): void => {
  const makes = allVehicles.map((v) => v.make)
    .filter((m, i, arr) => arr.indexOf(m) === i).sort();
  const sel = document.getElementById('filter-make') as HTMLSelectElement | null;
  if (!sel) return;
  sel.innerHTML = '<option value="">Any Make</option>';
  makes.forEach((m) => {
    sel.innerHTML += '<option value="' + m + '">' + m + '</option>';
  });
};

const populateModels = (make: string): void => {
  const sel = document.getElementById('filter-model') as HTMLSelectElement | null;
  if (!sel) return;
  sel.innerHTML = '<option value="">Any Model</option>';
  if (!make) return;
  const models = allVehicles.filter((v) => v.make === make)
    .map((v) => v.model)
    .filter((m, i, arr) => arr.indexOf(m) === i).sort();
  models.forEach((m) => {
    sel.innerHTML += '<option value="' + m + '">' + m + '</option>';
  });
};

/* ── Body style counts ── */
const updateBodyStyleCounts = (): void => {
  const styleMap: Record<string, string> = { suv: 'fc-suv', truck: 'fc-truck', sedan: 'fc-sedan', coupe: 'fc-coupe', van: 'fc-van', electric: 'fc-electric' };
  Object.keys(styleMap).forEach((style) => {
    const el = document.getElementById(styleMap[style]);
    if (!el) return;
    const count = allVehicles.filter((v) => matchesBodyStyle(v, style)).length;
    el.textContent = count > 0 ? String(count) : '';
  });
};

/* ── Condition tab highlighting ── */
const updateConditionTabs = (): void => {
  document.querySelectorAll<HTMLElement>('.condition-tab').forEach((tab) => {
    const cond = tab.dataset.condition;
    const active = cond === currentCondition;
    tab.style.background = active ? 'var(--primary)' : 'transparent';
    tab.style.color = active ? 'var(--white)' : 'var(--secondary)';
  });
};

/* ── Read URL params on load ── */
const readURLParams = (): void => {
  const p = new URLSearchParams(window.location.search);
  const condMap: Record<string, string> = { 'new': 'N', 'used': 'U', 'certified': 'C' };
  const condParam = p.get('condition');
  if (p.has('condition') && condParam && condMap[condParam]) {
    currentCondition = condMap[condParam];
  }
  if (p.has('make')) {
    const makeEl = document.getElementById('filter-make') as HTMLSelectElement | null;
    if (makeEl) { makeEl.value = p.get('make') ?? ''; populateModels(p.get('make') ?? ''); }
  }
  if (p.has('type')) {
    const cb = document.querySelector<HTMLInputElement>('.body-style-check[value="' + p.get('type') + '"]');
    if (cb) cb.checked = true;
  }
  if (p.has('maxPayment')) {
    const payEl = document.getElementById('filter-payment') as HTMLInputElement | null;
    if (payEl) payEl.value = p.get('maxPayment') ?? '';
  }
};

/* ── Event binding ── */
const bindEvents = (): void => {
  // Make cascade
  const makeEl = document.getElementById('filter-make') as HTMLSelectElement | null;
  if (makeEl) makeEl.addEventListener('change', () => {
    populateModels(makeEl.value);
    currentPage = 1; render();
  });

  // Standard selects / inputs
  ['filter-model','filter-year-min','filter-year-max','filter-price-min','filter-price-max','filter-mileage','filter-payment'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', () => { currentPage = 1; render(); });
  });

  // Body style checkboxes
  document.querySelectorAll<HTMLInputElement>('.body-style-check').forEach((cb) => {
    cb.addEventListener('change', () => { currentPage = 1; render(); });
  });

  // Exterior color checkboxes
  document.querySelectorAll<HTMLInputElement>('.color-check').forEach((cb) => {
    cb.addEventListener('change', () => { currentPage = 1; render(); });
  });

  // Sort
  const sortEl = document.querySelector('.sort-select');
  if (sortEl) sortEl.addEventListener('change', () => { currentPage = 1; render(); });

  // Clear filters
  const clearBtn = document.querySelector('.clear-filters');
  if (clearBtn) clearBtn.addEventListener('click', () => {
    currentCondition = '';
    document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.inv-filter').forEach((el) => {
      if (el.tagName === 'SELECT') el.value = '';
      else if ((el as HTMLInputElement).type === 'checkbox') (el as HTMLInputElement).checked = false;
      else el.value = '';
    });
    populateModels('');
    searchQuery = '';
    const si = document.getElementById('inv-search') as HTMLInputElement | null;
    if (si) si.value = '';
    currentPage = 1; render();
  });

  // Condition tabs
  document.querySelectorAll<HTMLElement>('.condition-tab').forEach((tab) => {
    tab.addEventListener('click', (e) => {
      e.preventDefault();
      currentCondition = tab.dataset.condition || '';
      currentPage = 1; render();
    });
  });

  // Search
  const searchEl = document.getElementById('inv-search') as HTMLInputElement | null;
  if (searchEl) {
    let timer: ReturnType<typeof setTimeout>;
    searchEl.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => { searchQuery = searchEl.value.trim(); currentPage = 1; render(); }, 250);
    });
    const searchBtn = document.querySelector('.inv-search-bar button');
    if (searchBtn) searchBtn.addEventListener('click', () => {
      searchQuery = searchEl.value.trim(); currentPage = 1; render();
    });
  }

  // View toggle (grid/list)
  const viewBtns = document.querySelectorAll<HTMLButtonElement>('.view-btn');
  const invMain  = document.querySelector('.inv-main');
  viewBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      viewBtns.forEach((b) => { b.classList.remove('active'); });
      btn.classList.add('active');
      invMain?.classList.toggle('inv-list', btn.title === 'List View');
    });
  });
};

/* ── Filter accordions ── */
const initFilterAccordions = (): void => {
  // Below the single-column cutover the sidebar stacks above the results, so open filters push
  // the inventory far down the page — start them collapsed there.
  const startCollapsed = window.matchMedia('(max-width: 960px)').matches;
  document.querySelectorAll<HTMLElement>('.filter-section').forEach((section) => {
    const header = section.querySelector<HTMLElement>('.filter-section-header');
    if (!header) return;
    if (startCollapsed) section.classList.add('collapsed');
    header.addEventListener('click', () => section.classList.toggle('collapsed'));
  });
};
initFilterAccordions();

/* ── Bootstrap ── */
loadInventory().then((data) => {
  allVehicles = data.inventory || [];
  populateMakes();
  readURLParams();
  bindEvents();
  render();
}).catch((err) => {
  console.error('Inventory load error:', err);
  const grid = document.getElementById('inventoryGrid');
  if (grid) grid.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:60px;">'
    + '<p style="color:var(--secondary);">Unable to load inventory. Please refresh or try again later.</p></div>';
});
